import { useRef, useState, type ReactNode } from 'react';
import { Tooltip as HeroTooltip } from '@heroui/react/tooltip';
import { cn } from '@/utils/cn';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * An info icon that holds an explanation the screen does not need to show.
 *
 * Explanatory copy — what a page is for, what a section counts — goes here
 * rather than under the heading. Read once, it is then noise on every later
 * visit, and it pushes the figures the page exists for down the screen.
 *
 * Opens on hover and keyboard focus like `Tooltip`, and additionally on press,
 * so it is reachable on touch where there is no hover. That is the difference
 * from wrapping an icon in `Tooltip`, and why this may carry copy that appears
 * nowhere else.
 *
 * NOT for anything the reader must see to act correctly — a failed
 * reconciliation, an overdue balance. Those stay on the page.
 */
export function InfoHint({
  content,
  label,
  placement = 'bottom',
  className,
}: {
  content: ReactNode;
  /** Accessible name for the icon. Defaults to "More information". */
  label?: string;
  placement?: 'top' | 'bottom' | 'start' | 'end';
  className?: string;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  // React Aria closes a tooltip on pointer-down, before the click lands. The
  // state as it was when the press began is what a press should toggle.
  const wasOpen = useRef(false);

  if (!content) return null;

  return (
    <HeroTooltip.Root isOpen={isOpen} onOpenChange={setIsOpen} delay={250}>
      <HeroTooltip.Trigger
        aria-label={label ?? t('common.moreInfo', 'More information')}
        className={cn(
          'ds-hit inline-flex size-6 shrink-0 cursor-help items-center justify-center rounded-full text-muted transition-colors hover:bg-neutral-100 hover:text-foreground',
          className,
        )}
        onPointerDownCapture={() => {
          wasOpen.current = isOpen;
        }}
        // Stopped so an icon inside a clickable row or card opens the hint
        // instead of following the row.
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!wasOpen.current);
        }}
      >
        <Info className="size-4" aria-hidden />
      </HeroTooltip.Trigger>
      <HeroTooltip.Content
        placement={placement}
        className="max-w-xs space-y-1.5 break-words text-start text-small font-normal"
      >
        {content}
      </HeroTooltip.Content>
    </HeroTooltip.Root>
  );
}
